import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box } from 'grommet';
import { useAppTheme } from '@Components/Context';
import { Seo } from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import { ClaimDetailsPartial, ClaimHistoryTablePartial } from '@Components/Partial/Product/ContentId';
import {
  initiateFetchClaimDetailsRequest,
  clearContentIdClaimDetailsRequest,
  initiateFetchClaimHistoryRequest,
  clearContentIdClaimHistoryRequest,
} from '@Actions';

const ClaimDetailsPage = ({
  small,
  loading,
  secondaryLoading,
  claimDetails,
  claimHistory,
  fetchClaimDetails,
  clearClaimDetails,
  fetchClaimHistory,
  clearClaimHistory,
  location,
}) => {
  const { id } = useParams();
  const { theme: { page }, isDarkMode } = useAppTheme();

  React.useEffect(() => {
    fetchClaimDetails(id);
    fetchClaimHistory(id);

    return () => {
      clearClaimDetails();
      clearClaimHistory();
    };
  }, [id]);

  const padding = small ? '1.5rem' : '2rem';
  const usePageBg = isDarkMode ? page.background : page.altBg;

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Claim details',
        subTitle: 'Content ID',
      }}
    >
      <Seo />
      <Box flex background={usePageBg} pad={{ vertical: padding }}>
        <Box pad={{ horizontal: padding, bottom: '2rem' }}>
          <ClaimDetailsPartial
            small={small}
            loading={loading}
            claim={claimDetails}
          />
        </Box>
        <ClaimHistoryTablePartial
          small={small}
          loading={secondaryLoading}
          historyData={claimHistory}
        />
      </Box>
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    claimDetails: state.contentIdClaims.claimDetails,
    claimHistory: state.contentIdClaims.claimHistory,
    loading: state.fetchLoader.dataLoading,
    secondaryLoading: state.fetchLoader.secondaryDataLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchClaimDetails: initiateFetchClaimDetailsRequest,
    clearClaimDetails: clearContentIdClaimDetailsRequest,
    fetchClaimHistory: initiateFetchClaimHistoryRequest,
    clearClaimHistory: clearContentIdClaimHistoryRequest,
  }, dispatch);
}

ClaimDetailsPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  secondaryLoading: PropTypes.bool.isRequired,
  fetchClaimDetails: PropTypes.func.isRequired,
  clearClaimDetails: PropTypes.func.isRequired,
  fetchClaimHistory: PropTypes.func.isRequired,
  clearClaimHistory: PropTypes.func.isRequired,
  claimDetails: PropTypes.shape({
    kind: PropTypes.string,
    id: PropTypes.string.isRequired,
    assetId: PropTypes.string,
    videoId: PropTypes.string,
    videoTitle: PropTypes.string,
    videoViews: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    policy: PropTypes.object,
    appliedPolicy: PropTypes.object,
    status: PropTypes.string,
    contentType: PropTypes.string,
    ugcType: PropTypes.string,
    timeCreated: PropTypes.string,
    timeStatusLastModified: PropTypes.string,
    blockOutsideOwnership: PropTypes.bool,
    matchInfo: PropTypes.shape({
      referenceId: PropTypes.string,
      longestMatch: PropTypes.object,
      totalMatch: PropTypes.object,
      matchSegments: PropTypes.arrayOf(PropTypes.object),
    }),
    origin: PropTypes.shape({
      source: PropTypes.string,
    }),
    isPartnerUploaded: PropTypes.bool,
    studioInfo: PropTypes.shape({
      claimUrl: PropTypes.string,
      videoUrl: PropTypes.string,
    }),
  }),
  claimHistory: PropTypes.shape({
    kind: PropTypes.string,
    id: PropTypes.string,
    event: PropTypes.arrayOf(PropTypes.shape({
      kind: PropTypes.string,
      time: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      typeDetails: PropTypes.object,
      reason: PropTypes.string,
      source: PropTypes.object,
    })),
  }),
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(ClaimDetailsPage));
